import Slide06Icon from '@assets/images/icons/onboarding/bubble-slide-6.svg';
import Slide02Icon from '@assets/images/icons/onboarding/bullseye-slide-2.svg';
import Slide09Icon from '@assets/images/icons/onboarding/check-slide-9.svg';
import Slide01Icon from '@assets/images/icons/onboarding/compass-slide-1.svg';
import Slide05Icon from '@assets/images/icons/onboarding/expert-slide-5.svg';
import Slide08Icon from '@assets/images/icons/onboarding/journal-slide-8.svg';
import Slide03Icon from '@assets/images/icons/onboarding/lifering-slide-3.svg';
import Slide04Icon from '@assets/images/icons/onboarding/seed-slide-4.svg';
import Slide10Icon from '@assets/images/icons/onboarding/stars-slide-10.svg';
import Slide07Icon from '@assets/images/icons/onboarding/videos-slide-7.svg';
import useWindowDimensions from '@hooks/useWindowDimensions';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { MotionImage } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  icon: any;
  gradient: string;
  bgImage?: string;
  conditionalKey?: 'speakUp' | 'experts' | 'instantHelp';
}

interface OnboardingSlidesProps {
  onComplete: () => void;
  companyFeatures: {
    speakUp: boolean;
    experts: boolean;
    instantHelp: boolean;
  };
}

const allSlides: Slide[] = [
  {
    id: 1,
    title: 'Discover Your Wellbeing Profile',
    subtitle: 'Understanding yourself is the first step to feeling better',
    description:
      "Take our research-backed assessments across mental, physical, social, career, and financial wellbeing. In just minutes, you'll get a complete picture of where you stand today. Your strengths, challenges, and opportunities for growth. No judgement, just insights.",
    icon: Slide01Icon,
    gradient: 'linear-gradient(135deg, #019df2 0%, #beaaf3 100%)',
  },
  {
    id: 2,
    title: 'Get Your Personal Action Plan',
    subtitle: 'Tailored guidance that adapts as you grow',
    description:
      "Based on your unique profile, we'll create a personalised roadmap filled with practical activities, expert videos, and bite-sized learning. As your wellbeing evolves and you use the platform more, we'll adapt your recommendations to keep supporting your journey. Choose your priorities and watch as we build a plan that grows with you.",
    icon: Slide02Icon,
    gradient: 'linear-gradient(135deg, #8eea36 0%, #06bf97 100%)',
  },
  {
    id: 3,
    title: 'Get Instant Help',
    subtitle: 'Support and resources when you need them most',
    description:
      'When you need immediate support, everything is right here in your personal space. Access our comprehensive resource library, connect with helpful tools and guidance, or reach out through our confidential support channels. Your support network is always available, exactly when you need it.',
    icon: Slide03Icon,
    gradient: 'linear-gradient(135deg, #ff8a4a 0%, #fcd300 100%)',
    conditionalKey: 'instantHelp',
  },
  {
    id: 4,
    title: 'Find Quick Calm & Support',
    subtitle: 'Quick tools to reset and recharge',
    description:
      'Feeling overwhelmed right now? Access immediate techniques through guided breathing exercises, quick stress-busters, mindfulness moments, and grounding activities. Sometimes all you need is 2 minutes to reset your day and feel more like yourself again.',
    icon: Slide04Icon,
    gradient: 'linear-gradient(135deg, #8eea36 0%, #47dcc5 100%)',
  },
  {
    id: 5,
    title: 'Connect With Professional Support',
    subtitle: 'Expert help whenever you need it',
    description:
      'Book confidential 1-on-1 sessions with qualified therapists, coaches, and counsellors, available 24/7. Whether you need someone to talk through a challenge or want structured support, professional help is always just a click away in your personal space.',
    icon: Slide05Icon,
    gradient: 'linear-gradient(135deg, #beaaf3 0%, #ff8a4a 100%)',
    conditionalKey: 'experts',
  },
  {
    id: 6,
    title: 'Speak Up Safely',
    subtitle: "Your voice matters, and it's completely anonymous",
    description:
      "Share what's really on your mind through our confidential speak-up channel. Whether it's workplace concerns, suggestions for improvement, or just getting something off your chest. You'll be heard without fear of judgement. This is your safe space to express yourself.",
    icon: Slide06Icon,
    gradient: 'linear-gradient(135deg, #47dcc5 0%, #019df2 100%)',
    conditionalKey: 'speakUp',
  },
  {
    id: 7,
    title: 'Explore Your Resource Library',
    subtitle: 'Everything you need in one place',
    description:
      'Discover our ever-growing collection of videos, articles, tools, and exercises from qualified professionals. From managing anxiety to improving sleep, building confidence to handling workplace stress. Find what you need, when you need it, all within your personal wellbeing space.',
    icon: Slide07Icon,
    gradient: 'linear-gradient(135deg, #019df2 0%, #06bf97 100%)',
  },
  {
    id: 8,
    title: 'Journal & Track Progress',
    subtitle: 'Your completely private space for reflection and growth',
    description:
      "Use your personal journalling feature to capture thoughts, process emotions, celebrate wins, and notice patterns. Journalling helps clarify your thinking, reduces stress, and deepens self-awareness. Your reflections are entirely private, a safe space to understand your experiences and see how far you've come on your wellbeing journey.",
    icon: Slide08Icon,
    gradient: 'linear-gradient(135deg, #ff8a4a 0%, #beaaf3 100%)',
  },
  {
    id: 9,
    title: 'Check-In Regularly',
    subtitle: 'Small steps, big changes',
    description:
      "Pop in for quick wellbeing check-ins that take less than 2 minutes. Track how you're feeling across different areas of your life and watch your progress over time. The more you check in, the more you'll understand yourself.",
    icon: Slide09Icon,
    gradient: 'linear-gradient(135deg, #fcd300 0%, #ff8a4a 100%)',
  },
  {
    id: 10,
    title: 'Ready to Start Your Wellbeing Journey?',
    subtitle: 'Your better tomorrow starts with one small step today',
    description:
      "Begin with a quick check-in and holistic assessment to understand where you are right now, it's the perfect starting point to start building your wellbeing profile. From there, the platform is yours to explore however feels right for you. Whether you dive into your action plan, try relief exercises, or simply browse what's available, you're already taking care of yourself. We're here to support you every step of the way.",
    icon: Slide10Icon,
    gradient: 'linear-gradient(135deg, #beaaf3 0%, #019df2 100%)',
  },
];

export const OnboardingIntroduction: React.FC<OnboardingSlidesProps> = ({
  onComplete,
  companyFeatures,
}) => {
  const slides = useMemo(
    () =>
      allSlides.filter(
        (slide) =>
          !(
            (slide.conditionalKey === 'speakUp' && !companyFeatures.speakUp) ||
            (slide.conditionalKey === 'experts' && !companyFeatures.experts) ||
            (slide.conditionalKey === 'instantHelp' && !companyFeatures.instantHelp)
          ),
      ),
    [allSlides, companyFeatures.speakUp, companyFeatures.experts, companyFeatures.instantHelp],
  );

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const slideRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useWindowDimensions();

  const isTouchDevice =
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const nextSlide = useCallback(() => {
    if (isAnimating || currentSlide >= slides.length - 1) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(currentSlide + 1);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, currentSlide, slides.length]);

  const prevSlide = useCallback(() => {
    if (isAnimating || currentSlide <= 0) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(currentSlide - 1);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, currentSlide]);

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 300);
  };

  const handleStart = (clientX: number) => {
    if (isAnimating) return;
    setIsDragging(true);
    setStartX(clientX);
    setCurrentX(clientX);
    setDragOffset(0);
  };

  const handleMove = (clientX: number) => {
    if (!isDragging || isAnimating) return;
    const deltaX = clientX - startX;
    setCurrentX(clientX);
    setDragOffset(deltaX);
  };

  const handleEnd = () => {
    if (!isDragging || isAnimating) return;
    setIsDragging(false);

    const deltaX = currentX - startX;
    const threshold = 80;

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && currentSlide > 0) {
        prevSlide();
      } else if (deltaX < 0 && currentSlide < slides.length - 1) {
        nextSlide();
      }
    }

    setStartX(0);
    setCurrentX(0);
    setDragOffset(0);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isTouchDevice) return;
    handleStart(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isTouchDevice) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    if (isTouchDevice) return;
    handleEnd();
  };

  const handleMouseLeave = () => {
    if (isTouchDevice) return;
    handleEnd();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === 'Escape') {
        onComplete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevSlide, nextSlide, onComplete]);

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  if (!currentSlideData) return null;

  return (
    <div className={styles.slideContainer}>
      <div
        className={styles.background}
        style={{
          background: currentSlideData.gradient,
        }}
      >
        <div className={styles.backgroundOverlay} />
      </div>
      ={' '}
      <div className={styles.backgroundElements}>
        <div className={`${styles.bgElement} ${styles.bgElement1}`} />
        <div className={`${styles.bgElement} ${styles.bgElement2}`} />
        <div className={`${styles.bgElement} ${styles.bgElement3}`} />
      </div>
      <div className={styles.mainContent}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.slideCounter}>
              {currentSlide + 1} of {slides.length}
            </div>
            <button onClick={onComplete} className={styles.skipButton}>
              Skip
            </button>
          </div>

          <div className={styles.progressContainer}>
            <div
              className={styles.progressBar}
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            />
          </div>
        </div>

        <div
          ref={slideRef}
          className={`${styles.slideContent} ${isDragging ? styles.dragging : ''}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={isDragging ? handleMouseMove : undefined}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{
            transform: isDragging ? `translateX(${dragOffset}px)` : undefined,
          }}
        >
          <div className={styles.slideWrapper}>
            <div
              className={`${styles.slideCard} ${
                isAnimating ? styles.slideExit : styles.slideEnter
              } ${!isDragging ? styles.dragTransition : ''}`}
            >
              <div className={styles.card}>
                <div className={styles.cardOverlay} />

                <div className={styles.cardContent}>
                  <div className={styles.iconContainer}>
                    <div
                      className={styles.icon}
                      style={{
                        background: currentSlideData.gradient,
                      }}
                    >
                      <MotionImage
                        src={currentSlideData.icon}
                        alt=""
                        aria-hidden="true"
                        className={styles.iconImage}
                        style={{ filter: 'brightness(0) invert(1)' }}
                        shape="rectangle"
                        skeletonHeight="25px"
                        skeletonWidth="25px"
                      />
                    </div>
                  </div>

                  <h1 className={styles.title}>{currentSlideData.title}</h1>

                  <p className={styles.subtitle}>{currentSlideData.subtitle}</p>

                  <p className={styles.description}>{currentSlideData.description}</p>

                  {isLastSlide && (
                    <SystemButton size="large" className={styles.ctaButton} onClick={onComplete}>
                      <PlayCircleIcon className={styles.ctaIcon} />
                      Start Your Journey
                    </SystemButton>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.navigation}>
              <div className={styles.navigationContent}>
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className={`${styles.navButton} ${styles.prevButton} ${
                    currentSlide === 0 ? styles.disabled : ''
                  }`}
                >
                  <NavigateBeforeIcon className={styles.navIcon} />
                  {isDesktop && <>Previous&nbsp;&nbsp;&nbsp;</>}
                </button>

                <div className={styles.indicators}>
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`${styles.indicator} ${
                        index === currentSlide ? styles.indicatorActive : ''
                      }`}
                    />
                  ))}
                </div>

                {!isLastSlide ? (
                  <button
                    onClick={nextSlide}
                    className={`${styles.navButton} ${styles.nextButton}`}
                  >
                    {isDesktop && <>&nbsp;&nbsp;&nbsp;Next</>}
                    <NavigateNextIcon className={styles.navIcon} />
                  </button>
                ) : (
                  <div className={styles.spacer} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.bottomSpacer} />
      </div>
    </div>
  );
};
